
import { createContext, useContext } from "react";
import { Session } from "@supabase/supabase-js";
import { useLocation, Navigate } from "react-router-dom";
import { useSessionManagement } from "@/hooks/useSessionManagement";
import { useOAuthHandler } from "@/hooks/useOAuthHandler";
import { isPublicRoute } from "@/utils/authUtils";

interface AuthContextType {
  session: Session | null;
  loading: boolean;
  handleOAuthRedirect: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ 
  session: null, 
  loading: true,
  handleOAuthRedirect: async () => {} 
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { session, loading } = useSessionManagement();
  const { handleOAuthRedirect } = useOAuthHandler();
  const location = useLocation();
  
  console.log("AuthProvider rendering:", { 
    session: session ? "User logged in" : "No session", 
    loading,
    path: location.pathname,
    isPublic: isPublicRoute(location.pathname)
  });

  if (!loading && !session && !isPublicRoute(location.pathname)) {
    console.log("No session found, redirecting to auth from:", location.pathname);
    return <Navigate to="/auth" />;
  }

  return (
    <AuthContext.Provider value={{ session, loading, handleOAuthRedirect }}>
      {children}
    </AuthContext.Provider>
  );
}
